import { Link, Typography, makeStyles } from '@material-ui/core'
import pluralize from 'pluralize'
import React from 'react'
import roundTo from 'round-to'

import { useAuth } from 'src/modules/auth'
import { useActionRunner } from 'src/modules/notification'
import { Button, FormikDialog, ProductList, SidePanel } from 'src/modules/ui'
import { formatPrice } from 'src/utils'

import { useSuppliers } from '../hooks'
import AddProductsModal from './AddProductsModal'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    padding: theme.spacing(1, 4, 4),
    flexDirection: 'column-reverse',
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
      paddingTop: theme.spacing(1),
    },
  },
  content: {
    flexGrow: 1,
  },
  productTable: {
    maxWidth: 600,
    width: '100%',
  },
  sideButton: {
    '& + &': {
      marginTop: theme.spacing(2),
    },
  },
  sidePanelContainer: {
    flexShrink: 0,
    marginLeft: 0,
    marginBottom: theme.spacing(4),
    width: 'auto',
    [theme.breakpoints.up('tablet')]: {
      marginLeft: theme.spacing(4),
      width: 250,
    },
  },
  unapproveText: {
    maxWidth: 400,
    '& > p': {
      '&:last-child': {
        marginBottom: 0,
      },
      marginBottom: theme.spacing(2),
    },
  },
}))

const Agreement = ({
  id,
  agreementProducts,
  canAddProducts,
  canApproveAgreement,
  deliveryTime,
  discount,
  document,
  minimumOrder,
  notes,
  orderCutoff,
  paymentTerms,
  status,
  supplier,
}) => {
  const classes = useStyles()
  const { user } = useAuth()
  const { approveAgreement, unapproveAgreement } = useSuppliers()

  const [doApprove] = useActionRunner(approveAgreement, {
    successNotification: {
      image: 'leeks',
      message: `Congratulations! We've added ${
        supplier.name
      } to your approved suppliers list.${
        user.organisation.environmentCount > 1
          ? '\n\nYou can now add their products to your purchasing environments.'
          : ''
      }`,
    },
  })
  const [doUnapprove] = useActionRunner(unapproveAgreement, {
    successNotification: 'Supplier has been unapproved',
  })

  const proposal = status === 'proposed'
  const accepted = status === 'accepted'

  const makePrice = (allDiscount, { discount, price, overridePrice }) => {
    let currentPrice = undefined
    if (overridePrice) {
      currentPrice = Number(overridePrice)
    } else if (discount) {
      currentPrice = price * ((100 - discount) / 100)
    } else if (allDiscount) {
      currentPrice = price * ((100 - allDiscount) / 100)
    }
    if (currentPrice !== undefined) {
      currentPrice = roundTo(currentPrice, 2)
    }
    return currentPrice
  }

  // Flatten agreement products
  const products = agreementProducts.map(
    ({ product, environments, ...ap }) => ({
      ...ap,
      ...product,
      overridePrice: makePrice(discount, { ...ap, ...product }),
      environments,
    })
  )

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <ProductList
          products={products}
          tableClassName={classes.productTable}
        />
      </div>
      <div className={classes.sidePanelContainer}>
        <SidePanel>
          {discount && (
            <SidePanel.Item title="Discount On All Products">
              <Typography variant="body2">-{discount}%</Typography>
            </SidePanel.Item>
          )}
          <SidePanel.Item title="Minimum Order">
            <Typography variant="body2">
              {Number(minimumOrder) ? formatPrice(minimumOrder) : 'None'}
            </Typography>
          </SidePanel.Item>
          <SidePanel.Item title="Order Cutoff Time">
            <Typography variant="body2">By {orderCutoff}</Typography>
          </SidePanel.Item>
          <SidePanel.Item title="Delivery Time">
            <Typography variant="body2">
              {pluralize('day', deliveryTime, true)}
            </Typography>
          </SidePanel.Item>
          <SidePanel.Item title="Payment Terms">
            {pluralize('day', paymentTerms, true)}
          </SidePanel.Item>
          {notes && (
            <SidePanel.Item title="Notes">
              <Typography variant="body2">{notes}</Typography>
            </SidePanel.Item>
          )}
          <SidePanel.Item title="Agreement Document">
            <Typography variant="body2">
              <Link href={document} target="_blank">
                View document
              </Link>
            </Typography>
          </SidePanel.Item>
          {proposal ? (
            canApproveAgreement && (
              <FormikDialog
                onSubmit={() => doApprove(id)}
                submitText="Approve"
                trigger={props => (
                  <Button
                    className={classes.sideButton}
                    fullWidth
                    color="orange"
                    {...props}
                  >
                    Approve Supplier
                  </Button>
                )}
              >
                <Typography>Approve agreement with supplier?</Typography>
              </FormikDialog>
            )
          ) : canAddProducts &&
            accepted &&
            user.organisation.environmentCount > 1 ? (
            <AddProductsModal
              agreementId={id}
              products={products}
              trigger={props => (
                <Button
                  className={classes.sideButton}
                  fullWidth
                  color="lightGreen"
                  {...props}
                >
                  Add Products To Environments
                </Button>
              )}
            />
          ) : null}
          {accepted && canApproveAgreement && (
            <FormikDialog
              onSubmit={() => doUnapprove(id)}
              submitText="Unapprove"
              trigger={props => (
                <Button
                  className={classes.sideButton}
                  fullWidth
                  color="orange"
                  {...props}
                >
                  Unapprove Supplier
                </Button>
              )}
            >
              <div className={classes.unapproveText}>
                <Typography>Unapprove agreement with supplier?</Typography>
                <Typography>
                  This will move agreement back to proposed and remove all draft
                  orders with this supplier
                </Typography>
              </div>
            </FormikDialog>
          )}
        </SidePanel>
      </div>
    </div>
  )
}

export default Agreement
